import * as React from 'react'

import { clsx } from 'clsx'

import { Avatar } from '@buffer-mono/popcorn'

import styles from './Notes.module.css'

export type NotesFeedProps = React.ComponentPropsWithRef<'section'>
export type NotesFeedListProps = React.ComponentPropsWithRef<'ul'>
export type NotesFeedFooterProps = React.ComponentPropsWithRef<'footer'>
export type NotesFeedEmptyProps = React.ComponentPropsWithRef<'div'>
export type NotesFeedStatusProps = React.ComponentPropsWithRef<'output'>

export type NoteItemProps = React.ComponentPropsWithRef<'li'>
export type NoteItemAvatarProps = React.ComponentPropsWithRef<'div'> & {
  src?: string
  name: string
}
export type NoteItemBodyProps = React.ComponentPropsWithRef<'div'>
export type NoteItemHeaderProps = React.ComponentPropsWithRef<'div'>
export type NoteItemAuthorProps = React.ComponentPropsWithRef<'span'>
export type NoteItemTimestampProps = React.ComponentPropsWithRef<'time'>
export type NoteItemEditedProps = React.ComponentPropsWithRef<'span'>
export type NoteItemContentProps = React.ComponentPropsWithRef<'div'>

const NotesFeedRoot = React.forwardRef<HTMLElement, NotesFeedProps>(
  (
    { children, className, 'aria-label': ariaLabel = 'Notes', ...props },
    ref,
  ) => (
    <section
      ref={ref}
      aria-label={ariaLabel}
      className={clsx(styles.feed, className)}
      {...props}
    >
      {children}
    </section>
  ),
)

const List = React.forwardRef<HTMLUListElement, NotesFeedListProps>(
  ({ children, className, ...props }, ref) => (
    <ul ref={ref} className={clsx(styles.list, className)} {...props}>
      {children}
    </ul>
  ),
)

const Footer = React.forwardRef<HTMLElement, NotesFeedFooterProps>(
  ({ children, className, ...props }, ref) => (
    <footer ref={ref} className={clsx(styles.footer, className)} {...props}>
      {children}
    </footer>
  ),
)

const Empty = React.forwardRef<HTMLDivElement, NotesFeedEmptyProps>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={clsx(styles.empty, className)} {...props}>
      {children}
    </div>
  ),
)

const Status = React.forwardRef<HTMLOutputElement, NotesFeedStatusProps>(
  ({ children, className, ...props }, ref) => (
    <output
      ref={ref}
      className={clsx(styles.status, className)}
      aria-live="polite"
      aria-atomic="true"
      {...props}
    >
      {children}
    </output>
  ),
)

const NoteItemRoot = React.forwardRef<HTMLLIElement, NoteItemProps>(
  ({ children, className, ...props }, ref) => (
    <li ref={ref} className={clsx(styles.note, className)} {...props}>
      {children}
    </li>
  ),
)

const NoteAvatar = React.forwardRef<HTMLDivElement, NoteItemAvatarProps>(
  ({ className, name, src, ...props }, ref) => (
    <div ref={ref} className={clsx(styles.avatar, className)} {...props}>
      <Avatar
        src={src?.trim() || undefined}
        alt=""
        fallback="letter"
        fallbackName={name}
        size="xxsmall"
      />
    </div>
  ),
)

const Body = React.forwardRef<HTMLDivElement, NoteItemBodyProps>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={clsx(styles.body, className)} {...props}>
      {children}
    </div>
  ),
)

const Header = React.forwardRef<HTMLDivElement, NoteItemHeaderProps>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={clsx(styles.header, className)} {...props}>
      {children}
    </div>
  ),
)

const Author = React.forwardRef<HTMLSpanElement, NoteItemAuthorProps>(
  ({ children, className, ...props }, ref) => (
    <span
      ref={ref}
      className={clsx(styles.author, className)}
      translate="no"
      {...props}
    >
      {children}
    </span>
  ),
)

const Timestamp = React.forwardRef<HTMLTimeElement, NoteItemTimestampProps>(
  ({ children, className, ...props }, ref) => (
    <time ref={ref} className={clsx(styles.timestamp, className)} {...props}>
      {children}
    </time>
  ),
)

const Edited = React.forwardRef<HTMLSpanElement, NoteItemEditedProps>(
  ({ children, className, ...props }, ref) => (
    <span ref={ref} className={clsx(styles.edited, className)} {...props}>
      {children}
    </span>
  ),
)

const Content = React.forwardRef<HTMLDivElement, NoteItemContentProps>(
  ({ children, className, ...props }, ref) => (
    <div ref={ref} className={clsx(styles.content, className)} {...props}>
      {children}
    </div>
  ),
)

NotesFeedRoot.displayName = 'NotesFeed'
List.displayName = 'NotesFeed.List'
Footer.displayName = 'NotesFeed.Footer'
Empty.displayName = 'NotesFeed.Empty'
Status.displayName = 'NotesFeed.Status'
NoteItemRoot.displayName = 'NoteItem'
NoteAvatar.displayName = 'NoteItem.Avatar'
Body.displayName = 'NoteItem.Body'
Header.displayName = 'NoteItem.Header'
Author.displayName = 'NoteItem.Author'
Timestamp.displayName = 'NoteItem.Timestamp'
Edited.displayName = 'NoteItem.Edited'
Content.displayName = 'NoteItem.Content'

export const NotesFeed = Object.assign(NotesFeedRoot, {
  List,
  Footer,
  Empty,
  Status,
})
export const NoteItem = Object.assign(NoteItemRoot, {
  Avatar: NoteAvatar,
  Body,
  Header,
  Author,
  Timestamp,
  Edited,
  Content,
})
